import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@helios/authz';
import { apiClient, type RequestOptions } from '@/services/apiClient';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import type { MapPoint } from './atlasModel';

export type SourceState = 'available' | 'partial' | 'unavailable' | 'not_configured' | 'restricted';
export interface IntelligenceSource {
  id: string;
  name: string;
  state: SourceState;
  detail: string;
  url: string;
}
export interface LocationMetric {
  id: string;
  label: string;
  value: number | null;
  margin: number | null;
  unit: string;
}
export interface GrowthObservation {
  id: string;
  label: string;
  previous: number | null;
  current: number | null;
  change: number | null;
  percent: number | null;
  changeMargin: number | null;
  distinguishable: boolean | null;
}
export interface ParcelRecord {
  id: string;
  parcelNumber: string | null;
  address: string | null;
  owners: string[];
  acres: number | null;
  landUse: string | null;
  refreshedAt: string | null;
  sourceUrl: string | null;
  zoning: {
    code: string | null;
    description: string | null;
    type: string | null;
    subtype: string | null;
    ordinanceUrl: string | null;
    dataDate: string | null;
  };
}
export interface LocationIntelligence {
  retrievedAt: string;
  growth?: {
    geography: string | null;
    previousPeriod: string;
    currentPeriod: string;
    basis: string;
    metrics: GrowthObservation[];
  };
  daytime?: {
    geography: string | null;
    period: string;
    residents: number | null;
    residentWorkers: number | null;
    workplaceWorkers: number | null;
    population: number | null;
    margin: number | null;
    netCommuters: number | null;
    method: string;
  };
  parcels?: { records: ParcelRecord[]; coverage: string; truncated: boolean };
  location:
    | (MapPoint & {
        label: string;
        precision: 'address_range' | 'selected_coordinate';
        stateFips: string | null;
        countyFips: string | null;
        tract: string | null;
        geography: string | null;
      })
    | null;
  demographics: { period: string; geography: string | null; metrics: LocationMetric[] };
  businesses: {
    period: string;
    geography: string | null;
    industries: Array<{
      code: string;
      label: string;
      establishments: number | null;
      employees: number | null;
    }>;
  };
  flood: {
    zones: Array<{ zone: string; subtype: string | null; specialHazard: boolean | null }>;
    coverage: string;
  };
  sources: IntelligenceSource[];
}
export type LayerKind =
  | 'demographics'
  | 'businesses'
  | 'development'
  | 'flood'
  | 'parcels'
  | 'access'
  | 'suppliers'
  | 'amenities';
export interface LayerResult {
  kind: LayerKind;
  state: SourceState;
  detail: string;
  retrievedAt: string;
  source: string;
  url: string;
  attribution: string;
  features: GeoJSON.FeatureCollection;
}
export interface ResourceLayer {
  generatedAt: string;
  crews: SourceState;
  equipment: SourceState;
  projects: Array<{
    projectId: string;
    crewCount: number | null;
    assignedHeadcount: number | null;
    equipmentCount: number | null;
  }>;
}
export type LocationRequest = { address: string } | { latitude: number; longitude: number };
async function get<T>(
  path: string,
  values: Record<string, string>,
  options: RequestOptions,
): Promise<T> {
  const response = await apiClient.get<T>(path + '?' + new URLSearchParams(values), options);
  if (!response.ok || !response.data)
    throw new Error(response.error ?? 'Location source is unavailable.');
  return response.data;
}
export const atlasApi = {
  intelligence: (request: LocationRequest, options: RequestOptions) =>
    get<LocationIntelligence>(
      '/maps/location-intelligence',
      Object.fromEntries(Object.entries(request).map(([k, v]) => [k, String(v)])),
      options,
    ),
  layer: (kind: LayerKind, point: MapPoint, radius: number, options: RequestOptions) =>
    get<LayerResult>(
      '/maps/layers',
      {
        kind,
        latitude: String(point.latitude),
        longitude: String(point.longitude),
        radius: String(radius),
      },
      options,
    ),
  resources: (options: RequestOptions) => get<ResourceLayer>('/maps/resources', {}, options),
};
export function useLocationIntelligence(request: LocationRequest | null) {
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  const key = JSON.stringify(request);
  const query = useQuery({
    queryKey: ['project-atlas', ...sessionScopeKey(scope.identity), 'location', key],
    queryFn: async ({ signal }) => {
      if (!request || !scope.isCurrent(scope.identity))
        throw new Error('Current map access is required.');
      const data = await atlasApi.intelligence(request, {
        signal,
        sessionOrigin: scope.identity.sessionOrigin,
        allowRefresh: false,
      });
      signal.throwIfAborted();
      if (!scope.isCurrent(scope.identity)) throw new Error('Map access changed.');
      return data;
    },
    enabled: scope.enabled && !!request,
    staleTime: 300_000,
    gcTime: 0,
    retry: false,
  });
  return {
    ...query,
    data:
      scope.enabled && scope.isCurrent(scope.identity) && !query.isError ? query.data : undefined,
  };
}
export function useAtlasLayer(
  kind: LayerKind,
  point: MapPoint | null,
  radius: number,
  active: boolean,
) {
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  const query = useQuery({
    queryKey: [
      'project-atlas',
      ...sessionScopeKey(scope.identity),
      'layer',
      kind,
      point?.latitude,
      point?.longitude,
      radius,
    ],
    queryFn: async ({ signal }) => {
      if (!point || !scope.isCurrent(scope.identity)) throw new Error('Select a location first.');
      const data = await atlasApi.layer(kind, point, radius, {
        signal,
        sessionOrigin: scope.identity.sessionOrigin,
        allowRefresh: false,
      });
      signal.throwIfAborted();
      if (!scope.isCurrent(scope.identity)) throw new Error('Map access changed.');
      return data;
    },
    enabled: scope.enabled && active && !!point,
    staleTime: 300_000,
    gcTime: 0,
    retry: false,
  });
  return {
    ...query,
    data:
      scope.enabled && active && scope.isCurrent(scope.identity) && !query.isError
        ? query.data
        : undefined,
  };
}
export function useResourceLayer(active: boolean) {
  const scope = useSessionScopeFence(PERMISSIONS.PROJECTS_READ);
  const query = useQuery({
    queryKey: ['project-atlas', ...sessionScopeKey(scope.identity), 'resources'],
    queryFn: async ({ signal }) => {
      if (!scope.isCurrent(scope.identity)) throw new Error('Current map access is required.');
      const data = await atlasApi.resources({
        signal,
        sessionOrigin: scope.identity.sessionOrigin,
        allowRefresh: false,
      });
      signal.throwIfAborted();
      if (!scope.isCurrent(scope.identity)) throw new Error('Map access changed.');
      return data;
    },
    enabled: scope.enabled && active,
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
  });
  return {
    ...query,
    data:
      scope.enabled && active && scope.isCurrent(scope.identity) && !query.isError
        ? query.data
        : undefined,
  };
}
